"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AgendaItem = void 0;
const ui_1 = require("@app/component/ui");
const Dropdown_1 = require("@app/component/ui/dropdown/Dropdown");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const use_debounce_1 = require("@app/util/forms/use-debounce");
const maybe_1 = require("@app/util/functional/maybe");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const shift_context_1 = require("../shift-context");
const use_available_shift_sections_1 = require("../use-available-shift-sections");
const firstOverlapIsAgenda = (t) => 'agendaFieldId' in t.firstOverlap;
const secondOverlapIsAgenda = (t) => 'agendaFieldId' in t.secondOverlap;
const AgendaItem = ({ index, field, error, update, overlaps, }) => {
    const { formControl, disabledEditation, transferStatus } = (0, shift_context_1.useShift)();
    const period = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'period' });
    const positionsOnWS = (0, store_1.useRootSelector)((state) => state.positions?.filter((p) => !p.archived) ?? []);
    const positionOptions = react_1.default.useMemo(() => new maybe_1.Maybe(positionsOnWS)
        .map((_positions) => _positions
        .map((p) => ({ ...p, name: p.name ?? '-' }))
        .map((p) => ({ value: p.id ?? '', label: react_1.default.createElement(ui_1.Position, { pos: p }) })))
        .get(() => []), [positionsOnWS]);
    const agendaNameOnChange = react_1.default.useCallback((newName) => {
        update(index, {
            start: field.start,
            duration: field.duration,
            name: newName,
            positionId: field.positionId,
            customAttributes: field.customAttributes,
        });
    }, [
        update,
        index,
        field.duration,
        field.start,
        field.positionId,
        field.customAttributes,
    ]);
    const { value: agendaNameValueDebounced, onChange: agendaNameOnChangeDebounced } = (0, use_debounce_1.useDebounce)({
        onChange: agendaNameOnChange,
        value: field.name ?? '',
        ms: 500,
    });
    const agendaStartOnChange = react_1.default.useCallback((newStart) => update(index, {
        start: newStart,
        duration: field.duration,
        name: field.name,
        positionId: field.positionId,
        customAttributes: field.customAttributes,
    }), [
        update,
        index,
        field.duration,
        field.name,
        field.positionId,
        field.customAttributes,
    ]);
    const { value: agendaStartValueDebouned, onChange: agendaStartOnChangeDebounced } = (0, use_debounce_1.useDebounce)({
        onChange: agendaStartOnChange,
        value: field.start ?? 0,
        ms: 750,
    });
    const agendaDurationOnChange = react_1.default.useCallback((newDuration) => update(index, {
        start: field.start,
        duration: newDuration,
        name: field.name,
        positionId: field.positionId,
        customAttributes: field.customAttributes,
    }), [
        update,
        index,
        field.start,
        field.name,
        field.positionId,
        field.customAttributes,
    ]);
    const { value: agendaDurationValueDebounced, onChange: agendaDurationOnChangeDebounced, } = (0, use_debounce_1.useDebounce)({
        onChange: agendaDurationOnChange,
        value: field.duration ?? 0,
        ms: 750,
    });
    const shiftSections = (0, use_available_shift_sections_1.useAvailableShiftSections)();
    const shiftAttributes = shiftSections.filter((a) => a.attribute?.type === 'boolean');
    const attribOptions = shiftAttributes.map((a) => ({
        value: a.attribute?.id ?? '',
        label: a.attribute?.name,
    }));
    // only display this as an exception for specific organization that needs it. normally it's supposed to be deprecated.
    const attribDropdown = util_1.miscUtil.getFilteredContentForOrganization('custom_attribute_on_agenda', null, {
        customContent: (react_1.default.createElement(Dropdown_1.Dropdown, { label: (0, util_1.t)('SHIFT_FLAGS'), options: attribOptions, singleSelect: true, type: 'variable', size: 'full-width', style: 'light', disabled: (0, util_1.isDefined)(disabledEditation), value: field?.customAttributes?.length
                ? attribOptions.find((a) => Array.isArray(field.customAttributes) &&
                    field.customAttributes[0]?.attributeId === a.value)
                : undefined, onChange: (v) => {
                let newCustomAttribs = [];
                if (v.value !== '') {
                    newCustomAttribs = [{ value: true, attributeId: v.value }];
                }
                update(index, {
                    start: field.start,
                    duration: field.duration,
                    name: field.name,
                    positionId: field.positionId,
                    customAttributes: newCustomAttribs,
                });
            }, ...error })),
    });
    const startError = overlaps
        ?.filter(secondOverlapIsAgenda)
        .find((overlap) => overlap.secondOverlap.agendaFieldId === field.agendaFieldId);
    const endError = overlaps
        ?.filter(firstOverlapIsAgenda)
        .find((overlap) => overlap.firstOverlap.agendaFieldId === field.agendaFieldId);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        attribDropdown,
        util_1.miscUtil.getFilteredContentForOrganization('agenda_instance_name', react_1.default.createElement(ui_1.Input, { label: (0, util_1.t)('NAME_V2'), type: 'text', value: agendaNameValueDebounced, onChange: agendaNameOnChangeDebounced, size: 'full-width', disabled: (0, util_1.isDefined)(disabledEditation) })),
        react_1.default.createElement("div", { style: {
                visibility: transferStatus === 'none' ? undefined : 'hidden',
            } },
            react_1.default.createElement(dropdown_input_1.DropdownInput, { options: positionOptions, label: (0, util_1.t)('POSITION'), control: formControl, name: `agenda.${index}.positionId`, disabled: (0, util_1.isDefined)(disabledEditation) })),
        react_1.default.createElement(ui_1.Input, { type: 'time', label: (0, util_1.t)('AGENDA_START'), size: 'full-width', disabled: (0, util_1.isDefined)(disabledEditation), value: (0, moment_1.default)(period.start)
                .add(agendaStartValueDebouned, 'minutes')
                .format('HH:mm'), onChange: (val) => {
                let startMoment = (0, moment_1.default)(`${(0, moment_1.default)(period.start).format('YYYY-MM-DD')} ${val}`, 'YYYY-MM-DD HH:mm');
                if (startMoment.startOf('minute').isBefore(period.start.startOf('minute')))
                    startMoment = startMoment.clone().add(1, 'day');
                const newStart = startMoment
                    .startOf('minute')
                    .diff(period.start.startOf('minute'), 'minutes');
                agendaStartOnChangeDebounced(newStart);
            }, ...((0, util_1.isDefined)(startError) ? error : {}) }),
        react_1.default.createElement(ui_1.Input, { type: 'time', label: (0, util_1.t)('AGENDA_END'), size: 'full-width', disabled: (0, util_1.isDefined)(disabledEditation), value: (0, moment_1.default)(period.start)
                .add(agendaStartValueDebouned + agendaDurationValueDebounced, 'minutes')
                .format('HH:mm'), onChange: (val) => {
                const endMoment = (0, moment_1.default)(`${(0, moment_1.default)(period.start).format('YYYY-MM-DD')} ${val}`, 'YYYY-MM-DD HH:mm');
                let newDuration = endMoment
                    .startOf('minute')
                    .diff(period.start.startOf('minute'), 'minutes') -
                    agendaStartValueDebouned;
                if (newDuration <= 0)
                    newDuration += 1440;
                if (newDuration > 1440)
                    newDuration -= 1440;
                agendaDurationOnChangeDebounced(newDuration);
            }, ...((0, util_1.isDefined)(endError) ? error : {}) })));
};
exports.AgendaItem = AgendaItem;
